import { forwardRef, Injectable } from '@angular/core'
import {
  AccountInfo,
  CreateAccountParams,
  CreateAccountResult,
  CreateAccountUserParams,
  PersonaInfo,
  ProfileInfo,
  UserDuplicateLookupParams
} from '@se-po/shared-data-access-models'
import { DobParser, PhoneParser } from '@se-po/shared-data-access-parsers'
import { BehaviorSubject } from 'rxjs'
import { UserServiceResources as us } from 'se-resource-types'

@Injectable({
  providedIn: 'root',
  useExisting: forwardRef(() => AccountStoreService),
})
export abstract class StoreService {
  public abstract address?: us.Address
  public abstract accountInfo: AccountInfo
  public abstract authSession: us.AuthSession
  public abstract basePath: string
  public abstract captchaResponse: string
  public abstract childAddressSame?: boolean
  public abstract childInfo?: ProfileInfo | PersonaInfo
  public abstract childPersona?: us.Persona
  public abstract createdAccount: BehaviorSubject<CreateAccountResult | null>
  public abstract draftGuardianInvites?: BehaviorSubject<any[]>
  public abstract invite: us.Invitation
  public abstract nextPath: string
  public abstract queryStringEmail?: string
  public abstract get disableEmail(): boolean
  public abstract get dupes(): us.UserDuplicateLookupResponse
  public abstract get dupesChecked(): boolean
  public abstract get createAccountParams(): CreateAccountParams
  public abstract get dateOfBirth(): string
  public abstract get userDuplicateLookupParams(): UserDuplicateLookupParams
  public abstract get emailAddress(): string
  public abstract get authSessionEmailAddress(): string
  public abstract get accountValid(): boolean
  public abstract get createdAccountValid(): boolean
  public abstract set dupes(d: us.UserDuplicateLookupResponse)
  public abstract patchAccountInfo(data: any): void
  public abstract patchChildInfo(data: any): void
  public abstract reset(): void
}

@Injectable({
  providedIn: 'root'
})
export class AccountStoreService implements StoreService {

  public accountInfo: AccountInfo
  public authSession: us.AuthSession
  public basePath = '/account'
  public captchaResponse: string
  public createdAccount = new BehaviorSubject<CreateAccountResult | null>(null)
  public invite: us.Invitation
  public nextPath: string
  public queryStringEmail?: string
  private _dupes: us.UserDuplicateLookupResponse
  private _dupesChecked = false

  public get disableEmail(): boolean {
    // we will prefill email from admin_invites, but allow them to be changed
    return !!this.authSessionEmailAddress && this.invite?.originator_type !== 'admin_invite'
  }

  public get dupes(): us.UserDuplicateLookupResponse {
    return this._dupes
  }

  public get dupesChecked(): boolean {
    return this._dupesChecked
  }

  public get createAccountParams(): CreateAccountParams {
    const user: CreateAccountUserParams = {
      allow_special_offers: this.accountInfo?.specialOffers ? '1' : '0',
      allow_third_party_mailing: this.accountInfo?.thirdPartyMailing ? '1' : '0',
      date_of_birth: DobParser.mmToIso(this.dateOfBirth),
      first_name: this.accountInfo.firstName,
      last_name: this.accountInfo.lastName,
      password: this.accountInfo.password,
      phone: PhoneParser.toN(this.accountInfo.phone),
    }
    if (!this.disableEmail) {
      user.email_on_create = this.accountInfo.email
    }

    return {
      user,
      'g-recaptcha-response': this.captchaResponse
    }
  }

  public get dateOfBirth(): string {
    return this.accountInfo.dateOfBirth
  }

  public get userDuplicateLookupParams(): UserDuplicateLookupParams {
    return {
      first_name: this.accountInfo.firstName,
      last_name: this.accountInfo.lastName,
      email: this.emailAddress,
      date_of_birth: DobParser.mmToIso(this.accountInfo.dateOfBirth),
      phone: PhoneParser.toN(this.accountInfo.phone),
      originator: 'account_create',
    }
  }

  public get emailAddress(): string {
    return this.accountInfo?.email || this.authSessionEmailAddress
  }

  public get authSessionEmailAddress(): string {
    const email: any = this.authSession?.email_address || this.authSession?.email_on_create

    // There was a scenario where auth_session.email_address
    // could be either a string or an object.
    // This has been updated to always be a string now.
    // Leaving this as-is for now since its not hurting anything, and you know, just in case
    if (typeof email === 'object') return email?.address
    if (typeof email === 'string') return email
    return this.queryStringEmail
  }

  public get accountValid(): boolean {
    if (!this.accountInfo) return false
    if (!this.accountInfo.firstName) return false
    if (!this.emailAddress) return false
    if (!this.dupesChecked) return false

    return true
  }

  public get createdAccountValid(): boolean {
    if (!this.createdAccount) return false
    if (!this.createdAccount.value?.uuid) return false

    return true
  }

  public set dupes(d: us.UserDuplicateLookupResponse) {
    this._dupes = d
    this._dupesChecked = true
  }

  public patchAccountInfo(data: any): void {
    this.accountInfo = { ...(this.accountInfo || {}), ...data }
  }

  public patchChildInfo(data: Partial<ProfileInfo>): void {
    // not implemented
  }

  public reset(): void {
    this.accountInfo = undefined
    this._dupes = undefined
    this._dupesChecked = false
    if (this.createdAccount) {
      Object.keys(this.createdAccount).forEach(
        key => delete (this.createdAccount as any)[key]
      )
    }
  }
}
